/**
 * A date field for the todo form: PickerField showing "Wed, Aug 12" (or, `withYear`, "Thu, Dec 31,
 * 2026"), with the platform date picker. Keys in, keys out — the Date only exists for the picker.
 */
import React from 'react';
import {
  formatDateWithYear,
  formatDayHeading,
  parseDateKey,
  toDateKey,
} from '@/utils/residents/todoCalendar';
import { PickerField } from './PickerField';

interface DateFieldProps {
  /**
   * Spoken name: "Due date" → "Due date: Wed, Aug 12" (on web the input is
   * named "Due date" and speaks its own value). The visible heading is the
   * form's, not this field's.
   */
  label: string;
  /** What the iOS Done closes: "due date" → "Done choosing due date". */
  pickerName: string;
  /** 'YYYY-MM-DD'. */
  value: string;
  /** Native: whether the picker is up. Web keeps it mounted regardless. */
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (key: string) => void;
  /** Show the year — for an end date that can sit months away. */
  withYear?: boolean;
}

export const DateField: React.FC<DateFieldProps> = ({
  label,
  pickerName,
  value,
  open,
  onToggle,
  onClose,
  onChange,
  withYear = false,
}) => {
  const date = parseDateKey(value);
  const shown = withYear ? formatDateWithYear(date) : formatDayHeading(date);
  return (
    <PickerField
      label={label}
      shown={shown}
      pickerName={pickerName}
      mode="date"
      pickerValue={date}
      open={open}
      onToggle={onToggle}
      onClose={onClose}
      onPick={picked => onChange(toDateKey(picked))}
    />
  );
};

DateField.displayName = 'DateField';
